/**
 * See: https://developers.google.com/publisher-tag/reference for
 * general API reference.
 */
import Script from 'next/script';
import PropTypes from 'prop-types';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import Flex from 'shopper/components/Flex';

import useMediaQuery from 'hooks/useMediaQuery';

import { getFromLocalStorage } from 'lib/localStorage';
import { parseToBool } from 'lib/string';

import APP from 'constants/app';
import LOCAL_STORAGE from 'constants/localStorage';

const isUserAllowedToReceivePersonalizedAds = parseToBool(
  getFromLocalStorage(LOCAL_STORAGE.IS_POLICY_NUMBER_3_ACCEPTED)
);

const AD_MANAGER_NETWORK_CODE = 21977757450;
const INTERVAL_TIME_IN_MILLISECONDS = 600;
const INTERVAL_TIME_RUNS_LIMIT_TO_TIMEOUT = 20;

const toCompleteSlotPath = (adPath) => `/${AD_MANAGER_NETWORK_CODE}${adPath}`;

const isValidBreakpointValue = (value) => value !== null && value !== undefined;

const GooglePublisherTagAdSlot = forwardRef(
  (
    {
      adId,
      adPath,
      adSizes,
      adResponsiveSizesMap,
      showAdSlotOnBreakpoints,
      ...rest
    },
    ref
  ) => {
    const adSlotRef = useRef(null);
    const { isSm, isMd, isLg, isXl } = useMediaQuery();
    const [isPubAdsReady, setPubAdsReady] = useState(false);
    const [isPubAdsServiceReady, setPubAdsServiceReady] = useState(false);
    const [willPubAdsScriptLoad, setWillPubAdsScriptLoad] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        isReady: isPubAdsReady,
        willScriptLoad: willPubAdsScriptLoad,
      }),
      [isPubAdsReady, willPubAdsScriptLoad]
    );

    useEffect(() => {
      if (showAdSlotOnBreakpoints.length === 0) {
        setWillPubAdsScriptLoad(true);
      }

      const breakpoints = [isSm, isMd, isLg, isXl];
      const currBreakpointIndex = breakpoints.lastIndexOf(true);

      if (showAdSlotOnBreakpoints.length === breakpoints.length) {
        const currBreakpointValue =
          showAdSlotOnBreakpoints[currBreakpointIndex];

        if (currBreakpointValue !== null) {
          setWillPubAdsScriptLoad(currBreakpointValue);
          return;
        }
      }

      for (
        let index = showAdSlotOnBreakpoints.length - 1;
        index >= 0;
        index--
      ) {
        if (index > currBreakpointIndex) {
          continue;
        }

        if (isValidBreakpointValue(showAdSlotOnBreakpoints[index])) {
          setWillPubAdsScriptLoad(showAdSlotOnBreakpoints[index]);
          break;
        }
      }
    }, []);

    useEffect(() => {
      if (isPubAdsServiceReady || !isPubAdsReady) {
        return;
      }

      if (window.googletag?.pubadsReady) {
        setPubAdsServiceReady(true);
        return;
      }

      let intervalRunIndex = 0;

      const isGoogleTagApiReadyIntervalId = setInterval(() => {
        if (window.googletag?.pubadsReady) {
          setPubAdsServiceReady(true);
          clearInterval(isGoogleTagApiReadyIntervalId);
        }

        intervalRunIndex += 1;

        if (intervalRunIndex === INTERVAL_TIME_RUNS_LIMIT_TO_TIMEOUT) {
          clearInterval(isGoogleTagApiReadyIntervalId);
        }
      }, INTERVAL_TIME_IN_MILLISECONDS);

      return () => {
        clearInterval(isGoogleTagApiReadyIntervalId);
      };
    }, [isPubAdsReady, isPubAdsServiceReady]);

    useEffect(() => {
      return () => {
        if (adSlotRef.current !== null && isPubAdsServiceReady) {
          window.googletag.pubads().clear([adSlotRef.current]);
        }
      };
    }, [isPubAdsServiceReady]);

    useEffect(() => {
      // Will be `true` once per `adId`
      const isAdIdAlreadyInDisplayedSlot = window.googletag?.pubadsReady
        ? window.googletag
            .pubads()
            .getSlots()
            .some((slot) => adId === slot.getSlotId().getDomId())
        : false;

      if (!isAdIdAlreadyInDisplayedSlot && isPubAdsReady) {
        window.googletag = window.googletag || { cmd: [] };
        const { googletag } = window;

        googletag.cmd.push(() => {
          adSlotRef.current = googletag
            .defineSlot(toCompleteSlotPath(adPath), adSizes, adId)
            .addService(googletag.pubads());

          if (adResponsiveSizesMap.length > 0) {
            const sizeMappingBuilder = googletag.sizeMapping();

            adResponsiveSizesMap.forEach(({ viewport, sizesMap }) => {
              sizeMappingBuilder.addSize(viewport, sizesMap);
            });

            const mapping = sizeMappingBuilder.build();

            adSlotRef.current.defineSizeMapping(mapping);
          }

          googletag.enableServices();
        });
      }
    }, [isPubAdsReady]);

    useEffect(() => {
      if (!isPubAdsServiceReady) {
        return;
      }

      window.googletag.cmd.push(() => {
        window.googletag.display(adId);
      });
    }, [isPubAdsServiceReady]);

    if (!willPubAdsScriptLoad) {
      return null;
    }

    return (
      <>
        <Script
          id="google-ad-manager"
          src={APP.GOOGLE_AD_MANAGER_URL}
          onReady={() => setPubAdsReady(true)}
        />
        <Flex overflow="hidden" id={adId} {...rest} />
        {/* Will be executed only once at the first
      `GooglePublisherTagAdSlot` rendered, setting some PubAds service
      configs */}
        <Script id="google-ad-manager-script-initial-config">
          {`
          window.googletag = window.googletag || {cmd: []};
          googletag.cmd.push(function() {
            googletag.pubads().setPrivacySettings({
              nonPersonalizedAds: ${isUserAllowedToReceivePersonalizedAds}
            })
            googletag.pubads().enableSingleRequest();
          });
        `}
        </Script>
      </>
    );
  }
);

GooglePublisherTagAdSlot.displayName = 'GooglePublisherTagAdSlot';

GooglePublisherTagAdSlot.defaultProps = {
  adResponsiveSizesMap: [],
  showAdSlotOnBreakpoints: [],
};

GooglePublisherTagAdSlot.propTypes = {
  adId: PropTypes.string.isRequired,
  /**
   * Reference: https://developers.google.com/publisher-tag/guides/get-started#ad-unit-path
   */
  adPath: PropTypes.string.isRequired,
  /**
   * Type reference:
   * - https://developers.google.com/publisher-tag/reference#googletag.GeneralSize
   */
  adSizes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      ),
    ])
  ).isRequired,
  /**
   * Type references:
   * - https://developers.google.com/publisher-tag/reference#googletag.SingleSizeArray
   * - https://developers.google.com/publisher-tag/reference#googletag.GeneralSize
   */
  adResponsiveSizesMap: PropTypes.arrayOf(
    PropTypes.shape({
      viewport: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.arrayOf(PropTypes.number),
        ])
      ).isRequired,
      sizesMap: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.arrayOf(PropTypes.number),
        ])
      ).isRequired,
    })
  ),
  /**
   * Set-up breakpoints where the ads are loaded or not.
   * The breakpoint order are: [sm, md, lg, xl]
   */
  showAdSlotOnBreakpoints: PropTypes.arrayOf(PropTypes.bool),
};

export default memo(GooglePublisherTagAdSlot);
